import React, { useState } from "react";
import styled from "styled-components";
import {
  deleteMedia,
  uploadMedia,
} from "../../database/media/cloudflareMedia";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
`;

const HiddenInput = styled.input`
  position: absolute;
  opacity: 0;
  pointer-events: none;
  width: 0;
  height: 0;
`;

const UploadLabel = styled.label<{ $disabled: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  height: 36px;
  padding: 0 var(--space-4);
  background: var(--color-surface);
  color: var(--color-text);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  font-family: var(--font-sans);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  cursor: ${(p) => (p.$disabled ? "not-allowed" : "pointer")};
  opacity: ${(p) => (p.$disabled ? 0.5 : 1)};
  transition:
    background var(--duration-fast) var(--ease-standard),
    border-color var(--duration-fast) var(--ease-standard);
  align-self: flex-start;

  &:hover {
    background: ${(p) =>
      p.$disabled ? "var(--color-surface)" : "var(--color-surface-subtle)"};
    border-color: ${(p) =>
      p.$disabled ? "var(--color-border)" : "var(--color-border-strong)"};
  }
`;

const PreviewCard = styled.div`
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: var(--space-3);
  display: flex;
  flex-direction: column;
  gap: var(--space-2);

  video {
    width: 100%;
    max-height: 200px;
    border-radius: var(--radius-sm);
    display: block;
  }
`;

const DeleteRow = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const DeleteButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-1);
  height: 28px;
  padding: 0 var(--space-3);
  background: transparent;
  color: var(--color-text-secondary);
  border: 1px solid transparent;
  border-radius: var(--radius-sm);
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition:
    background var(--duration-fast) var(--ease-standard),
    color var(--duration-fast) var(--ease-standard);

  &:hover {
    background: var(--color-danger-subtle);
    color: var(--color-danger);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const Spinner = styled.span`
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: ds-video-upload-spin 0.6s linear infinite;

  @keyframes ds-video-upload-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const UploadIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

const TrashIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
  </svg>
);

type VideoUploadProps = {
  field: {
    name: string;
    value: string;
  };
  form: {
    setFieldValue: (field: string, value: any) => void;
  };
  subactionId: string;
  reference: string;
};

const VideoUpload: React.FC<VideoUploadProps> = ({
  field,
  form,
  subactionId,
  reference,
}) => {
  const [uploading, setUploading] = useState(false);

  const deleteOldVideo = async (url: string) => {
    if (url) {
      try {
        await deleteMedia({ url });
      } catch (error) {
        console.error("Error deleting old video:", error);
      }
    }
  };

  const handleVideoChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.currentTarget.files?.[0];
    if (file) {
      setUploading(true);
      try {
        const previousUrl = field.value;
        const uploaded = await uploadMedia({
          body: file,
          kind: "video",
          fileName: file.name,
          firebasePath: `${reference}/${subactionId}/${file.name}`,
        });
        form.setFieldValue(field.name, uploaded.url);
        if (previousUrl && previousUrl !== uploaded.url) {
          void deleteOldVideo(previousUrl);
        }
      } catch (error) {
        console.error("Error uploading video:", error);
      } finally {
        setUploading(false);
      }
    }
  };

  const handleDelete = async () => {
    if (field.value) {
      await deleteOldVideo(field.value);
    }
    form.setFieldValue(field.name, "");
  };

  const inputId = `video-upload-${field.name}`;

  return (
    <Wrapper>
      <HiddenInput
        accept="video/*"
        id={inputId}
        type="file"
        onChange={handleVideoChange}
        disabled={uploading}
      />
      <UploadLabel htmlFor={inputId} $disabled={uploading}>
        {uploading ? (
          <>
            <Spinner /> Uploading…
          </>
        ) : (
          <>
            <UploadIcon /> Upload video
          </>
        )}
      </UploadLabel>
      {field.value && (
        <PreviewCard>
          <video src={field.value} controls />
          <DeleteRow>
            <DeleteButton type="button" onClick={handleDelete}>
              <TrashIcon /> Remove
            </DeleteButton>
          </DeleteRow>
        </PreviewCard>
      )}
    </Wrapper>
  );
};

export default VideoUpload;
